import { AfterViewInit, Component, ElementRef, HostListener, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { UserDto } from 'src/app/shared/dto/user.dto';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzFormDirective, NzFormItemComponent, NzFormControlComponent, NzFormModule } from 'ng-zorro-antd/form';
import { NzRowDirective, NzColDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective, NzInputGroupComponent, NzInputGroupWhitSuffixOrPrefixDirective } from 'ng-zorro-antd/input';
import { DrawerMode } from 'src/app/core/enums/drawer-mode';
import { DRAWER_WIDTH_CHANGE_PLACEMENT, WIDTH_VIEW_LIST_COMPONENT_ACTIONS } from 'src/app/core/constants/components';
import { NzIconModule, NzIconService } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { PauseReasonDto } from 'src/app/shared/dto/pause-reason.dto';
import { PauseReasonsResponse, PauseReasonsService } from '../../services/pause-reasons.service';
import { TITLE_TABLE_SIZE } from 'src/app/core/constants/style';
import { IconDefinition } from '@ant-design/icons-angular';
import { PlusOutline } from '@ant-design/icons-angular/icons';
import _ from 'lodash';
import { NzModalService } from 'ng-zorro-antd/modal';
import { HHmmssToMillis, millisToHHmmss, millisToString, showDialog } from 'src/app/shared/utils/utils';
import { DefaultComponent } from 'src/app/shared/services/view.service';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTransferComponent, TransferDirection, TransferItem } from 'ng-zorro-antd/transfer';
import { DirectionsEnum } from 'src/app/shared/enums/directions.enum';
import { MachineDto } from 'src/app/shared/dto/machine.dto';
import { MachineService } from '../../services/machine.service';

const icons: IconDefinition[] = [
    PlusOutline
];
@Component({
    selector: 'app-pause-reasons',
    standalone: true,
    imports: [
        NzPageHeaderModule,
        NzSpaceModule,
        NzDividerModule,
        NzDrawerModule,
        NzDatePickerModule,
        FormsModule,
        NzMessageModule,
        NzTableModule,
        NzSelectModule,
        NzInputDirective,
        ReactiveFormsModule,
        NzFormDirective,
        NzRowDirective,
        NzFormItemComponent,
        NzColDirective,
        NzFormControlComponent,
        NzInputDirective,
        NzFormModule,
        NzIconModule,
        NzToolTipModule,
        NzIconModule,
        NzInputGroupComponent,
        NzInputGroupWhitSuffixOrPrefixDirective,
        LoaderComponent,
        NzCheckboxModule,
        NzButtonModule,
        NzTabsModule,
        NzTransferComponent
    ],
    templateUrl: './pause-reasons.component.html',
    styleUrl: './pause-reasons.component.scss'
})

export class PauseReasonsComponent extends DefaultComponent implements OnInit, AfterViewInit, OnDestroy {

    totalPauseReasons:number = 0;
    isLoading: boolean = true;
    pageSize:number = 10;
    pageIndex:number = 1;
    filterText: string = '';
    isSaving: boolean = false;

    fieldsReadOnly: boolean = true;
    drawerTitle: string = '';
    drawerMode!: DrawerMode;
    drawerVisible: boolean = false;
    pauseReasonSelected: PauseReasonDto | null = null;
    pauseReasons: PauseReasonDto[] = [];
    currentUser: UserDto = new UserDto();

    tableHeight!: number;
    innerWidth: number = 0;
    DRAWER_WIDTH_CHANGE_PLACEMENT = DRAWER_WIDTH_CHANGE_PLACEMENT;
    WIDTH_VIEW_LIST_COMPONENT_ACTIONS = WIDTH_VIEW_LIST_COMPONENT_ACTIONS;

    machinesTransferItems: TransferItem[] = [];
    machines: MachineDto[] = [];
    machinesSelectedIds: number[] = [];

    validateForm: FormGroup<{
        name: FormControl<string>;
        isDefault: FormControl<boolean>;
        hours: FormControl<number>;
        minutes: FormControl<number>;
        seconds: FormControl<number>;
        timeStimated: FormControl<number>;
        machines: FormControl<MachineDto[]>;
    }>;

    @ViewChild('tableContainer') private readonly _tableContainer!: ElementRef;

    private readonly pauseReasonsService = inject(PauseReasonsService);
    private readonly machineService = inject(MachineService);
    private readonly message = inject(NzMessageService);
    private readonly userLoggedService = inject(UserLoggedService);
    private readonly fb = inject(NonNullableFormBuilder);
    private readonly iconService = inject(NzIconService);
    private readonly modalService = inject(NzModalService);

    private unsubscribe$ = new Subject<void>();
    private unsubscribeById$: Subject<void> | null = null;

    constructor() {
        super('pauseReasons', '');
        this.validateForm = this.fb.group({
            name: ['', [Validators.required]],
            isDefault: [false],
            hours: [0, [Validators.required, Validators.min(0)]],
            minutes: [0, [Validators.required, Validators.min(0), Validators.max(59)]],
            seconds: [0, [Validators.required, Validators.min(0), Validators.max(59)]],
            timeStimated: [0, [Validators.required, Validators.min(1000)]],
            machines: new FormControl<MachineDto[]>([], { nonNullable: true })
        });
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.innerWidth = window.innerWidth;
    }

    async ngOnInit() {
        this.iconService.addIcon(...icons);
        this.currentUser = this.userLoggedService.userLogged;
        this.innerWidth = window.innerWidth;

        await this.machineService.setupWebSocket();
            this.machineService.machines$
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe(
                    {
                        next: (machines) => {
                            this.machines = machines;
                        },
                        error: () => {
                            this.message.error('Error cargando máquinas');
                        }
                    }
                );

        await this.pauseReasonsService.setupWebSocketPaginated(this.pageSize, this.pageIndex);
        this.pauseReasonsService.pauseReasonsPaginated$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (response: PauseReasonsResponse) => {
                    this.pauseReasons = _.cloneDeep(response.pauseReasons);
                },
                error: () => {
                    this.message.error('Error cargando motivos de pausa');
                }
            });

        this.validateForm.controls.hours.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(hours => {
            this.setTimeStimated(hours, this.validateForm.controls.minutes.value, this.validateForm.controls.seconds.value);
        });
        this.validateForm.controls.minutes.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(minutes => {
            this.setTimeStimated(this.validateForm.controls.hours.value, minutes, this.validateForm.controls.seconds.value);
        });
        this.validateForm.controls.seconds.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(seconds => {
            this.setTimeStimated(this.validateForm.controls.hours.value, this.validateForm.controls.minutes.value, seconds);
        });

        this.isLoading = false;
    }

    async ngAfterViewInit(): Promise<void> {
        this.innerWidth = window.innerWidth;
        setTimeout(() => {
            this.tableHeight =
                (this._tableContainer.nativeElement as HTMLImageElement)
                    .clientHeight - TITLE_TABLE_SIZE; // X depend of your page display
        });
    }

    ngOnDestroy(): void {
        this.pauseReasonsService.disconnectWebSocketPaginated();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();

        if (this.pauseReasonSelected) {
            this.pauseReasonsService.disconnectWebSocketById(this.pauseReasonSelected.id);

            this.unsubscribeById$?.complete();
        }

    }

    initTransferItemsComponent(): void {
        try {
            const transferItems: TransferItem[] = [];
            const availableMachines = this.machines.filter((machine) => {
                return !this.pauseReasonSelected?.machines?.some((m) => m.id === machine.id);
            });

            availableMachines.forEach((availableMachine) => {
                transferItems.push({
                    key: availableMachine.id,
                    title: availableMachine.name,
                    description: availableMachine.ip,
                    direction: DirectionsEnum.LEFT as TransferDirection,
                });
            });

            this.pauseReasonSelected?.machines?.forEach((assignedMachine) => {
                transferItems.push({
                    key: assignedMachine.id,
                    title: assignedMachine.name,
                    description: assignedMachine.ip,
                    direction: DirectionsEnum.RIGHT as TransferDirection,
                });
            });

            this.machinesTransferItems = transferItems;
        } catch (error) {
            this.message.error('Error inicializando el componente de asignación de máquinas');
        }
    }

    includeSelectedMachinesInForm(): void {
        try {
            this.machinesSelectedIds = [];
            this.machinesTransferItems.forEach((element) => {
                if (element.direction === DirectionsEnum.RIGHT as TransferDirection) {
                    this.machinesSelectedIds.push(element['key']);
                }
            });

            const machinesSelected = this.machines.filter(machine => this.machinesSelectedIds.includes(machine.id));
            this.validateForm.get('machines')?.setValue(machinesSelected);
        } catch (error) {
            this.message.error('Error incluyendo máquinas seleccionadas en el formulario');
        }
    }

    filterOptionsTransferMachines(inputValue: string, item: any): boolean {
        return item.title.toLocaleLowerCase().indexOf(inputValue.toLocaleLowerCase()) > -1;
    }

    async onQueryParamsChange(params: NzTableQueryParams): Promise<void> {
        const { pageSize, pageIndex, sort, filter } = params;

        this.pageSize = pageSize;
        this.pageIndex = pageIndex;

        await this.getPaginatedPauseReasons();
    }

    async clearFilter(): Promise<void> {
        this.filterText = '';
        this.pageIndex = 1;

        await this.getPaginatedPauseReasons();
    }

    async getPaginatedPauseReasons() {
        this.isLoading = true;
        const response = await this.pauseReasonsService.getPaginatedPauseReasons(this.pageSize, this.pageIndex, this.filterText);

        this.totalPauseReasons = response.total;
        this.pageIndex = response.page ?? this.pageIndex;
        this.pauseReasons = response.pauseReasons;
        this.pauseReasonsService.refreshVariables(this.pageSize, this.pageIndex, this.filterText);
        this.isLoading = false;
    }

    create() {
        this.drawerMode = DrawerMode.CREATE;
        this.drawerTitle = 'Creando nuevo motivo de pausa';
        this.fieldsReadOnly = false;
        this.validateForm.enable();
        this.initTransferItemsComponent();
        this.openDrawer();
    }

    async view(data: PauseReasonDto) {
        this.unsubscribeById$ = new Subject<void>();

        await this.pauseReasonsService.setupWebSocketById(data.id);
        this.pauseReasonsService.pauseReasonById$
            .pipe(
                takeUntil(this.unsubscribeById$)
            )
            .subscribe({
                next: (pauseReason: PauseReasonDto | { deleted:boolean } | null) => {
                    if (pauseReason && pauseReason instanceof PauseReasonDto ) {
                        this.setData(pauseReason);
                        this.message.info('Motivo de pausa actualizado por otro usuario');
                    }else if( pauseReason && pauseReason.deleted ){
                        this.message.info('Motivo de pausa eliminado por otro usuario');
                        this.closeDrawer();
                    }
                },
                error: () => {
                    this.message.error('Error actualizando información de motivo de pausa');
                }
            });

        this.setData(data);

        this.validateForm.disable();

        this.drawerMode = DrawerMode.VIEW;
        this.drawerTitle = 'Viendo motivo de pausa ' + this.pauseReasonSelected?.name;
        this.fieldsReadOnly = true;

        this.openDrawer();
    }

    async edit(data: PauseReasonDto) {
        this.unsubscribeById$ = new Subject<void>();

        await this.pauseReasonsService.setupWebSocketById(data.id);
        this.pauseReasonsService.pauseReasonById$
            .pipe(
                takeUntil(this.unsubscribeById$)
            )
            .subscribe({
                next: async (pauseReason: PauseReasonDto | { deleted:boolean } | null) => {
                    if (pauseReason && pauseReason instanceof PauseReasonDto ) {
                        if (pauseReason.updatedById !== this.currentUser.id) {
                            if (await showDialog(this.modalService, 'Motivo de pausa actualizado por otro usuario, ¿deseas cargar los datos actualizados?')) {
                                this.setData(pauseReason);
                                this.message.info('Formulario actualizado');
                            }
                        } else {
                            this.setData(pauseReason);
                        }
                    }else if( pauseReason && pauseReason.deleted ){
                        this.message.info('Motivo de pausa eliminado por otro usuario');
                        this.closeDrawer();
                    }
                },
                error: () => {
                    this.message.error('Error actualizando información de motivo de pausa');
                }
            });

        this.setData(data);

        this.validateForm.enable();

        this.drawerMode = DrawerMode.EDIT;
        this.drawerTitle = 'Editando motivo de pausa ' + this.pauseReasonSelected?.name;
        this.fieldsReadOnly = false;
        this.openDrawer();
    }

    async delete(id: number) {
        if (
            await showDialog(this.modalService, '¿Estás seguro de que quieres eliminar este motivo de pausa? Esta acción no se puede deshacer.')
        ) {
            this.pauseReasonsService.delete(id).subscribe({
                next: (pauseReason: PauseReasonDto) => {
                    try {
                        if (pauseReason == undefined) {
                            this.message.create('error', `No se ha podido eliminar el motivo de pausa, motivo de pausa no encontrado`);

                            return;
                        }

                        const index = this.pauseReasons.findIndex((p) => p.id === id);

                        if (index < 0) {
                            this.message.create('error', `No se ha podido eliminar el motivo ${pauseReason.name} con id ${id}, motivo de pausa no encontrado`);

                            return;
                        }

                        this.pauseReasons.splice(index, 1);
                        const data = [...this.pauseReasons];
                        this.pauseReasons = data;

                        this.message.create('success', 'Motivo de pausa eliminado correctamente');
                    } catch (error: any) {
                        this.message.create('error', `Error eliminando motivo de pausa. Error: ${error.message}`);
                    }
                },
                error: (error: any) => {
                    this.message.create('error', `Error eliminando motivo de pausa, error de servidor. Error: ${error.message}`);
                },
            });
        }
    }

    submitForm() {
        this.isSaving = true;
        this.includeSelectedMachinesInForm();

        const pauseReasonDto: PauseReasonDto = {
            id: this.pauseReasonSelected ? this.pauseReasonSelected.id : 0,
            name: this.validateForm.controls.name.value ? this.validateForm.controls.name.value : '',
            isDefault: this.validateForm.controls.isDefault.value ? this.validateForm.controls.isDefault.value : false,
            timeStimated: this.validateForm.controls.timeStimated.value ? this.validateForm.controls.timeStimated.value : 0,
            machines: this.validateForm.controls.machines.value ? this.validateForm.controls.machines.value : []
        };

        if (!this.validForm(pauseReasonDto)) {
            this.isSaving = false;
            return;
        }

        if (this.drawerMode === DrawerMode.CREATE) {
            this.pauseReasonsService.save(pauseReasonDto).subscribe({
                next: (pauseReason: PauseReasonDto) => {
                    try {
                        if (pauseReason == undefined) {
                            this.isSaving = false;
                            throw new Error('Error creating pause reason');
                        }

                        this.isSaving = false;
                        this.message.create('success', 'Motivo de pausa creado correctamente');

                        this.closeDrawer();
                    } catch (error: any) {
                        this.isSaving = false;
                        this.message.create('error', `Error creando motivo de pausa. Error: ${error.message}`);
                    }
                },
                error: (error: any) => {
                    this.isSaving = false;
                    this.message.create('error', `Error creando motivo de pausa, error de servidor. Error: ${error.message}`);
                }
            });
        } else if (this.drawerMode === DrawerMode.EDIT) {
            this.pauseReasonsService.update(pauseReasonDto.id, pauseReasonDto).subscribe({
                next: (pauseReason: PauseReasonDto) => {
                    try {
                        if (pauseReason == undefined) {
                            this.isSaving = false;
                            this.message.create('error', 'Error actualizando el motivo de pausa');

                            return;
                        }

                        const index = this.pauseReasons.findIndex(
                            (p) => p.id === pauseReason.id,
                        );

                        if (index < 0) {
                            this.isSaving = false;
                            this.message.create('error', `No se ha podido actualizar el motivo ${pauseReasonDto.name} con id ${pauseReasonDto.id}, motivo de pausa no encontrado`);

                            return;
                        }

                        this.pauseReasons[index] = pauseReason;
                        const data = [...this.pauseReasons];
                        this.pauseReasons = data;

                        this.isSaving = false;
                        this.message.create('success', 'Motivo de pausa actualizada correctamente');

                        this.closeDrawer();
                    } catch (error: any) {
                        this.isSaving = false;
                        this.message.create('error', `Error actualizando motivo de pausa. Error: ${error.message}`);
                    }
                },
                error: (error: any) => {
                    this.isSaving = false;
                    this.message.create('error', `Error actualizando motivo de pausa, error de servidor. Error: ${error.message}`);
                }
            });
        }
    }

    closeDrawer() {
        this.machinesSelectedIds = [];
        this.machinesTransferItems = [];
        if (this.drawerMode === DrawerMode.VIEW || this.drawerMode === DrawerMode.EDIT) {
            this.pauseReasonsService.disconnectWebSocketById(this.pauseReasonSelected!.id);

            this.unsubscribeById$?.complete();
        }
        this.drawerMode = DrawerMode.NONE;
        this.drawerVisible = false;
        this.pauseReasonSelected = null;
        this.validateForm.reset();
    }

    private openDrawer() {
        this.drawerVisible = true;
    }

    private validForm(cause: PauseReasonDto): boolean {
        if (!cause.name) {
            this.message.create('error', 'El nombre del motivo de la pausa es obligatorio');
            return false;
        }
        return true;
    }

    calculateMillisToHHmmss(millisec: number): string {
        return millisToString(millisec);
    }

    private setData(data: PauseReasonDto) {
        this.pauseReasonSelected = data;
        this.validateForm.patchValue(data);

        const millis = millisToHHmmss(data.timeStimated);
        this.validateForm.controls.hours.setValue(millis.hours);
        this.validateForm.controls.minutes.setValue(millis.minutes);
        this.validateForm.controls.seconds.setValue(millis.seconds);
        this.initTransferItemsComponent();
    }

    private setTimeStimated(hours: number, minutes: number, seconds: number) {
        this.validateForm.controls.timeStimated.setValue(HHmmssToMillis(hours, minutes, seconds));
        this.validateForm.controls.timeStimated.updateValueAndValidity();
        this.validateForm.controls.timeStimated.markAllAsTouched();
    }
}
